import { Injectable, inject } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { MasterService } from "./master.service";
import { ShotlistService } from "./shotlist.service";
import { jobCancelPayload, uniquePositiveJobIds, videoProductionCancelPayload } from "./job-status.util";

export type JobCancelKind = "llm" | "video";
export type AutonomousBatchStage = "moodboard" | "storyboard";

export function jobCancelConfirmCopy(
  kind: JobCancelKind = "llm",
  count = 1,
): {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
} {
  const many = count > 1;
  const noun = kind === "video" ? "video generation" : "generation";
  return {
    title: many ? `Cancel these ${noun}s?` : `Cancel this ${noun}?`,
    content:
      "This stops the job. Credits used for this run are not refunded.",
    okText: many ? "Cancel generations" : "Cancel generation",
    cancelText: "Keep generating",
  };
}

export function isAutonomousBatchCancellableStatus(status: unknown): boolean {
  const s = String(status ?? "")
    .toLowerCase()
    .trim();
  return (
    s === "pending" ||
    s === "processing" ||
    s === "paused" ||
    s === "in-progress"
  );
}

export function jobCancelBatchConfirmCopy(): {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
} {
  return {
    title: "Cancel this batch generation?",
    content:
      "This stops all queued and in-progress jobs in the batch. Credits used for this run are not refunded.",
    okText: "Cancel batch",
    cancelText: "Keep generating",
  };
}

export interface JobCancelResult {
  ok: boolean;
  alreadyCancelled: boolean;
  cannotCancel: boolean;
  notFound: boolean;
  job: any;
  message: string;
  cancelledJobIds: number[];
  alreadyCancelledJobIds: number[];
  blockedJobIds: number[];
}

const SKIP_ERROR_TOAST = { "X-Skip-Error-Toast": "1" };

@Injectable({ providedIn: "root" })
export class JobCancelService {
  private readonly master = inject(MasterService);
  private readonly shotlist = inject(ShotlistService);

  cancelLlmJobs(
    jobId: number | string | (number | string)[],
  ): Observable<JobCancelResult> {
    const ids = Array.isArray(jobId)
      ? uniquePositiveJobIds(jobId)
      : uniquePositiveJobIds([jobId]);
    if (!ids.length) {
      return of(this.emptyResult("job_id is required"));
    }
    return this.master
      .cancelScheduleCronJob(jobCancelPayload(ids), SKIP_ERROR_TOAST)
      .pipe(
        map((res) => this.parseSuccess(res)),
        catchError((err) => of(this.parseError(err))),
      );
  }

  cancelVideoJobs(
    jobId: number | string | (number | string)[],
  ): Observable<JobCancelResult> {
    const ids = Array.isArray(jobId)
      ? uniquePositiveJobIds(jobId)
      : uniquePositiveJobIds([jobId]);
    if (!ids.length) {
      return of(this.emptyResult("request_id is required"));
    }
    return this.shotlist
      .cancelVideoProduction(videoProductionCancelPayload(ids), SKIP_ERROR_TOAST)
      .pipe(
        map((res) => this.parseSuccess(res, ids)),
        catchError((err) => of(this.parseError(err, ids))),
      );
  }

  cancel(
    kind: JobCancelKind,
    jobId: number | string | (number | string)[],
  ): Observable<JobCancelResult> {
    return kind === "video"
      ? this.cancelVideoJobs(jobId)
      : this.cancelLlmJobs(jobId);
  }

  cancelAutonomousBatch(payload: {
    video_id: number | string;
    stage: AutonomousBatchStage | string;
    request_id?: number | string;
  }): Observable<JobCancelResult> {
    const videoId = Number(payload?.video_id);
    const stage = String(payload?.stage ?? "").trim();
    if (!Number.isFinite(videoId) || videoId <= 0 || !stage) {
      return of(this.emptyResult("Invalid param"));
    }
    const body: {
      video_id: number;
      stage: string;
      request_id?: number;
    } = { video_id: videoId, stage };
    const requestId = Number(payload.request_id);
    if (Number.isFinite(requestId) && requestId > 0) {
      body.request_id = requestId;
    }
    return this.shotlist.cancelAutonomousBatch(body, SKIP_ERROR_TOAST).pipe(
      map((res) => this.parseBatchSuccess(res)),
      catchError((err) => of(this.parseBatchError(err))),
    );
  }

  private emptyResult(message: string): JobCancelResult {
    return {
      ok: false,
      alreadyCancelled: false,
      cannotCancel: false,
      notFound: false,
      job: null,
      message,
      cancelledJobIds: [],
      alreadyCancelledJobIds: [],
      blockedJobIds: [],
    };
  }

  private parseSuccess(res: any, fallbackIds: number[] = []): JobCancelResult {
    const message = String(res?.message ?? "Job cancelled successfully");
    const alreadyCancelled = /already cancelled/i.test(message);
    const ok =
      res?.success === true ||
      res?.statusCode === 200 ||
      alreadyCancelled ||
      /cancelled successfully/i.test(message);
    const cancelledFromApi = uniquePositiveJobIds(
      this.asIdList(
        res?.data?.cancelled_job_ids ??
          res?.data?.cancelled_request_ids ??
          res?.data?.request_id,
      ),
    );
    return {
      ok,
      alreadyCancelled,
      cannotCancel: false,
      notFound: false,
      job: res?.data?.job ?? null,
      message,
      cancelledJobIds:
        cancelledFromApi.length > 0
          ? cancelledFromApi
          : ok
            ? fallbackIds
            : [],
      alreadyCancelledJobIds: uniquePositiveJobIds(
        this.asIdList(res?.data?.already_cancelled_job_ids),
      ),
      blockedJobIds: uniquePositiveJobIds(
        this.asIdList(res?.data?.blocked_job_ids),
      ),
    };
  }

  private parseError(err: any, _fallbackIds: number[] = []): JobCancelResult {
    const body = err?.error ?? err;
    const message = String(
      body?.message ?? body?.error?.message ?? err?.message ?? "",
    );
    return {
      ok: false,
      alreadyCancelled: /already cancelled/i.test(message),
      cannotCancel: /cannot be cancelled/i.test(message),
      notFound: err?.status === 404 || /not found/i.test(message),
      job: body?.data?.job ?? null,
      message,
      cancelledJobIds: uniquePositiveJobIds(
        this.asIdList(body?.data?.cancelled_job_ids),
      ),
      alreadyCancelledJobIds: uniquePositiveJobIds(
        this.asIdList(body?.data?.already_cancelled_job_ids),
      ),
      blockedJobIds: uniquePositiveJobIds(
        this.asIdList(body?.data?.blocked_job_ids),
      ),
    };
  }

  private parseBatchSuccess(res: any): JobCancelResult {
    const data = res?.data ?? {};
    const message = String(res?.message ?? "Batch cancelled successfully");
    const alreadyCancelled = /already cancelled/i.test(message);
    const cancelledJobIds = uniquePositiveJobIds(
      this.asIdList(data?.cancelled_job_ids),
    );
    const ok =
      res?.statusCode === 200 ||
      alreadyCancelled ||
      /cancelled successfully/i.test(message) ||
      cancelledJobIds.length > 0;
    return {
      ok,
      alreadyCancelled,
      cannotCancel: false,
      notFound: false,
      job: data,
      message,
      cancelledJobIds,
      alreadyCancelledJobIds: uniquePositiveJobIds(
        this.asIdList(data?.already_cancelled_job_ids),
      ),
      blockedJobIds: uniquePositiveJobIds(this.asIdList(data?.blocked_job_ids)),
    };
  }

  private parseBatchError(err: any): JobCancelResult {
    const body = err?.error ?? err;
    const message = String(
      body?.message ?? body?.error?.message ?? err?.message ?? "",
    );
    const status = Number(err?.status ?? body?.statusCode);
    const notFound =
      status === 404 || /no active batch found/i.test(message);
    return {
      ok: notFound,
      alreadyCancelled: notFound || /already cancelled/i.test(message),
      cannotCancel: /cannot be cancelled/i.test(message),
      notFound,
      job: body?.data ?? null,
      message,
      cancelledJobIds: uniquePositiveJobIds(
        this.asIdList(body?.data?.cancelled_job_ids),
      ),
      alreadyCancelledJobIds: uniquePositiveJobIds(
        this.asIdList(body?.data?.already_cancelled_job_ids),
      ),
      blockedJobIds: uniquePositiveJobIds(
        this.asIdList(body?.data?.blocked_job_ids),
      ),
    };
  }

  private asIdList(value: unknown): (number | string | null | undefined)[] {
    if (Array.isArray(value)) return value;
    if (value == null || value === "") return [];
    return [value as number | string];
  }
}
